import { Button, Tooltip, Typography } from "@suraasa/placebo-ui"
import { AttemptStatus } from "api/resources/assessments/types"
import clsx from "clsx"
import CheckRestrictions from "features/AccessManagement/CheckRestrictions"
import {
  reloadOnAssessmentClose,
  startAssessment,
} from "features/Assessments/helpers"
import { formatDate, formatDurationPrecise } from "utils/helpers"

import LiveCalendarIcon from "./CalendarIcon"
import { AssessmentSchedule } from "./useUpcomingPlannerItems"

const timeFormat = "do MMMM yyyy, h:mm a"

const getAssessmentDescription = (props: {
  title: string
  duration: string | null
  time: string
}) => {
  return [
    props.title.toLowerCase().includes("assessment") ? null : "Assessment",
    formatDate(props.time, timeFormat),
    props.duration ? formatDurationPrecise(props.duration) : null,
  ]
    .filter(Boolean)
    .join(" • ")
}

const LiveAssessmentCard = ({
  assessmentSchedule,
  className,
}: {
  assessmentSchedule: AssessmentSchedule
  className?: string
}) => {
  return (
    <CheckRestrictions
      accessStatus={assessmentSchedule.accessStatus}
      render={({ isRestricted }) => {
        const attempt = assessmentSchedule.attempt
        const isMissed = attempt?.status === AttemptStatus.MISSED
        const timeHasStarted =
          new Date(assessmentSchedule.startTime) < new Date()
        const canAttempt = !isMissed && timeHasStarted

        const disabled = isRestricted || !canAttempt

        const tooltipContent = isRestricted
          ? "Access Restricted"
          : isMissed
          ? "You missed this assessment"
          : !timeHasStarted
          ? `Starts at ${formatDate(assessmentSchedule.startTime, timeFormat)}`
          : ""

        return (
          <div className={clsx("flex flex-col p-2.5", className)}>
            <LiveCalendarIcon date={assessmentSchedule.startTime} />

            <div className="mb-1.5 flex flex-wrap items-end justify-between gap-2">
              <div className="flex flex-col">
                <Typography className="mb-0.5 mt-2 text-title4 text-onSurface-900">
                  {assessmentSchedule.assessment.title}
                </Typography>
                <Typography className="text-onSurface-600" variant="body">
                  {getAssessmentDescription({
                    title: assessmentSchedule.assessment.title,
                    duration: assessmentSchedule.duration,
                    time: assessmentSchedule.startTime,
                  })}
                </Typography>
              </div>

              <div className="w-full sm:w-fit">
                <Tooltip
                  enabled={Boolean(tooltipContent)}
                  content={tooltipContent}
                >
                  <Button
                    color="primary"
                    className="w-full"
                    size="sm"
                    disabled={disabled}
                    onClick={() => {
                      startAssessment({
                        assessmentId: assessmentSchedule.assessment.uuid,
                        onClose: reloadOnAssessmentClose,
                        userPlannerItemId: assessmentSchedule.id.toString(),
                      })
                    }}
                  >
                    Attempt Assessment
                  </Button>
                </Tooltip>
              </div>
            </div>

            <Typography className="text-muted" variant="smallBody">
              This final assessment counts toward your grade. Complete all
              course materials by{" "}
              <span className="text-strongSmallBody text-onSurface-700">
                {formatDate(
                  assessmentSchedule.startTime,
                  "MMMM d, yyyy 'at' h:mm a"
                )}
              </span>{" "}
              for the best score.
            </Typography>
          </div>
        )
      }}
    />
  )
}

export default LiveAssessmentCard
